import { StorageService } from './storage';

/**
 * Price Alert Service - Browser notifications for price targets
 * Supports:
 * - Token price alerts (above/below threshold)
 * - Token price change percentage alerts
 * - Token ATH alerts (when token reaches new all-time high)
 * - Portfolio daily change percentage alerts
 * - Portfolio ATH alerts (when portfolio reaches new all-time high)
 */

// Extended condition types to support all alert types
export type AlertCondition =
  | 'above'
  | 'below'
  | 'change_percent'
  | 'token_ath'
  | 'portfolio_change'
  | 'portfolio_ath';

// Alert scope - token or portfolio
export type AlertScope = 'token' | 'portfolio';

// Direction for portfolio change alerts
export type ChangeDirection = 'up' | 'down' | 'both';

export interface PriceAlert {
  id: string;
  tokenId: string;
  tokenName: string;
  condition: AlertCondition;
  targetPrice?: number;
  changePercent?: number; // +10% ou -5%
  basePrice?: number; // Prix de référence pour change_percent
  enabled: boolean;
  repeatable?: boolean; // Alerte répétable
  cooldownUntil?: string; // Cooldown jusqu'à cette date (ISO string)
  createdAt: string;
  triggeredAt?: string;
  notificationSent?: boolean;
  // New fields for portfolio & ATH alerts
  alertScope?: AlertScope;
  thresholdDirection?: ChangeDirection;
  athValue?: number; // Tracked ATH value for ATH alerts
  lastCheckedValue?: number; // Last value checked (for portfolio alerts)
}

export interface CurrentPrices {
  [tokenId: string]: {
    usd: number;
    usd_24h_change?: number;
  };
}

// Callback type for alert triggers (used for celebrations)
export type AlertTriggeredCallback = (alert: PriceAlert, message: string, currentPrice: number) => void;

class AlertService {
  private alerts: PriceAlert[] = [];
  private checkInterval?: number;
  private isInitialized = false;
  private notificationPermission: NotificationPermission = 'default';
  private onAlertTriggeredCallbacks: AlertTriggeredCallback[] = [];

  /**
   * Initialize alert service
   */
  async init(): Promise<boolean> {
    if (this.isInitialized) return true;

    // Load alerts from localStorage
    this.alerts = StorageService.get<PriceAlert[]>('price_alerts', []);

    // Request notification permission
    if ('Notification' in window) {
      this.notificationPermission = Notification.permission;

      if (this.notificationPermission === 'default') {
        // Don't request immediately, let user trigger it
        console.log('Notification permission not yet requested');
      }
    } else {
      console.warn('Browser notifications not supported');
      return false;
    }

    // Start checking alerts every 5 minutes
    this.startChecking();

    this.isInitialized = true;
    return true;
  }

  /**
   * Request notification permission from user
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission;

      if (permission === 'granted') {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Start checking alerts periodically
   */
  private startChecking() {
    // Check immediately
    this.checkAlerts().catch(console.error);

    // Then check every 5 minutes
    this.checkInterval = window.setInterval(() => {
      this.checkAlerts().catch(console.error);
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Stop checking alerts
   */
  stopChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  /**
   * Check all enabled alerts against current prices
   */
  async checkAlerts(currentPrices?: CurrentPrices): Promise<void> {
    if (!currentPrices) {
      // If no prices provided, alerts will be checked when prices are fetched
      return;
    }

    const now = new Date();

    // First, check for expired cooldowns and re-enable repeatable alerts
    this.alerts.forEach(alert => {
      if (alert.repeatable && alert.cooldownUntil) {
        const cooldownExpiry = new Date(alert.cooldownUntil);
        if (now >= cooldownExpiry) {
          // Cooldown expired, re-enable alert
          alert.enabled = true;
          alert.cooldownUntil = undefined;
          alert.notificationSent = false;
          alert.triggeredAt = undefined;
          console.log(`✅ Cooldown expired for alert ${alert.id}, re-enabled`);
        }
      }
    });

    // Filter enabled alerts (not in cooldown)
    const enabledAlerts = this.alerts.filter(a => {
      if (!a.enabled || a.notificationSent) return false;

      // Skip if in cooldown
      if (a.cooldownUntil) {
        const cooldownExpiry = new Date(a.cooldownUntil);
        if (now < cooldownExpiry) {
          return false;
        }
      }

      return true;
    });

    // Separate token alerts from portfolio alerts
    const tokenAlerts = enabledAlerts.filter(a =>
      a.alertScope !== 'portfolio' && a.tokenId !== 'portfolio'
    );

    // Check token-level alerts
    for (const alert of tokenAlerts) {
      const priceData = currentPrices[alert.tokenId];
      if (!priceData || priceData.usd === undefined) continue;

      const currentPrice = priceData.usd;
      const result = this.checkTokenAlertCondition(alert, currentPrice);

      if (result.triggered) {
        this.triggerAlert(alert, result.message, currentPrice);
      }
    }

    // Note: Portfolio alerts are checked by the CRON job on the server
    // as they require fetching all portfolio tokens and calculating total values

    // Save any cooldown changes
    this.save();
  }

  /**
   * Check token-level alert condition
   */
  private checkTokenAlertCondition(alert: PriceAlert, currentPrice: number): { triggered: boolean; message: string } {
    let triggered = false;
    let message = '';

    switch (alert.condition) {
      case 'above':
        if (alert.targetPrice !== undefined && currentPrice >= alert.targetPrice) {
          triggered = true;
          message = `Prix actuel: $${currentPrice.toFixed(2)} (cible: $${alert.targetPrice.toFixed(2)})`;
        }
        break;

      case 'below':
        if (alert.targetPrice !== undefined && currentPrice <= alert.targetPrice) {
          triggered = true;
          message = `Prix actuel: $${currentPrice.toFixed(2)} (cible: $${alert.targetPrice.toFixed(2)})`;
        }
        break;

      case 'change_percent':
        if (alert.changePercent !== undefined && alert.basePrice !== undefined) {
          const changePercent = ((currentPrice - alert.basePrice) / alert.basePrice) * 100;

          if (alert.changePercent > 0 && changePercent >= alert.changePercent) {
            triggered = true;
            message = `+${changePercent.toFixed(2)}% (cible: +${alert.changePercent}%)`;
          } else if (alert.changePercent < 0 && changePercent <= alert.changePercent) {
            triggered = true;
            message = `${changePercent.toFixed(2)}% (cible: ${alert.changePercent}%)`;
          }
        }
        break;

      case 'token_ath':
        // Check if current price is above tracked ATH
        if (alert.athValue === undefined || currentPrice > alert.athValue) {
          // Only trigger if we have a previous ATH to compare
          if (alert.athValue !== undefined) {
            triggered = true;
            const increase = ((currentPrice - alert.athValue) / alert.athValue) * 100;
            message = `Nouvel ATH! $${currentPrice.toFixed(2)} (+${increase.toFixed(2)}%)`;
          }
          // Update ATH value
          alert.athValue = currentPrice;
        }
        break;
    }

    return { triggered, message };
  }

  /**
   * Trigger an alert (send notification)
   */
  private triggerAlert(alert: PriceAlert, message: string, currentPrice: number) {
    // Determine notification title based on alert type
    let title = `🔔 Alerte Prix: ${alert.tokenName}`;

    if (alert.condition === 'token_ath' || alert.condition === 'portfolio_ath') {
      title = `🏆 Nouvel ATH: ${alert.condition === 'portfolio_ath' ? 'Portfolio' : alert.tokenName}`;
    } else if (alert.condition === 'portfolio_change') {
      title = `📊 Alerte Portfolio`;
    }

    // Browser notification
    if (this.notificationPermission === 'granted') {
      try {
        const notification = new Notification(title, {
          body: message,
          icon: '/icon-192x192.png',
          badge: '/icon-72x72.png',
          tag: alert.id,
          requireInteraction: false,
        });

        // Auto-close after 10 seconds
        setTimeout(() => notification.close(), 10000);
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    }

    // Mark alert as triggered
    alert.triggeredAt = new Date().toISOString();
    alert.notificationSent = true;

    // Handle repeatable vs one-time alerts
    if (alert.repeatable) {
      // Set cooldown period (1 hour)
      const cooldownDate = new Date();
      cooldownDate.setHours(cooldownDate.getHours() + 1);
      alert.cooldownUntil = cooldownDate.toISOString();

      // Update base price for change_percent alerts
      if (alert.condition === 'change_percent') {
        alert.basePrice = currentPrice;
      }

      console.log(`⏰ Repeatable alert ${alert.id} in cooldown until ${cooldownDate.toLocaleTimeString()}`);
    } else {
      // Disable one-time alerts after trigger
      alert.enabled = false;
      console.log(`❌ One-time alert ${alert.id} disabled after trigger`);
    }

    this.save();

    // 🎉 Call celebration callbacks
    this.onAlertTriggeredCallbacks.forEach(callback => {
      try {
        callback(alert, message, currentPrice);
      } catch (error) {
        console.error('Error in alert callback:', error);
      }
    });
  }

  /**
   * Register a callback for when alerts are triggered (for celebrations)
   */
  onAlertTriggered(callback: AlertTriggeredCallback): () => void {
    this.onAlertTriggeredCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.onAlertTriggeredCallbacks.indexOf(callback);
      if (index > -1) {
        this.onAlertTriggeredCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Create a new alert
   */
  createAlert(alert: Omit<PriceAlert, 'id' | 'createdAt' | 'enabled'>): PriceAlert {
    const newAlert: PriceAlert = {
      ...alert,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      enabled: true,
    };

    this.alerts.push(newAlert);
    this.save();

    return newAlert;
  }

  /**
   * Create a portfolio ATH alert
   */
  createPortfolioATHAlert(repeatable: boolean = true): PriceAlert {
    return this.createAlert({
      tokenId: 'portfolio',
      tokenName: 'Portfolio',
      condition: 'portfolio_ath',
      alertScope: 'portfolio',
      repeatable,
    });
  }

  /**
   * Create a portfolio daily change alert
   */
  createPortfolioChangeAlert(
    changePercent: number,
    direction: ChangeDirection = 'both',
    repeatable: boolean = true
  ): PriceAlert {
    return this.createAlert({
      tokenId: 'portfolio',
      tokenName: 'Portfolio',
      condition: 'portfolio_change',
      changePercent,
      thresholdDirection: direction,
      alertScope: 'portfolio',
      repeatable,
    });
  }

  /**
   * Create a token ATH alert
   */
  createTokenATHAlert(tokenId: string, tokenName: string, currentPrice?: number, repeatable: boolean = true): PriceAlert {
    return this.createAlert({
      tokenId,
      tokenName,
      condition: 'token_ath',
      athValue: currentPrice, // Initialize with current price if available
      targetPrice: currentPrice,
      repeatable,
    });
  }

  /**
   * Update an existing alert
   */
  updateAlert(id: string, updates: Partial<PriceAlert>): boolean {
    const index = this.alerts.findIndex(a => a.id === id);
    if (index === -1) return false;

    this.alerts[index] = {
      ...this.alerts[index],
      ...updates,
    };

    this.save();
    return true;
  }

  /**
   * Delete an alert
   */
  deleteAlert(id: string): boolean {
    const initialLength = this.alerts.length;
    this.alerts = this.alerts.filter(a => a.id !== id);

    if (this.alerts.length < initialLength) {
      this.save();
      return true;
    }

    return false;
  }

  /**
   * Toggle alert enabled state
   */
  toggleAlert(id: string): boolean {
    const alert = this.alerts.find(a => a.id === id);
    if (!alert) return false;

    alert.enabled = !alert.enabled;

    // Reset notification flag when re-enabling
    if (alert.enabled) {
      alert.notificationSent = false;
    }

    this.save();

    return true;
  }

  /**
   * Get all alerts
   */
  getAlerts(): PriceAlert[] {
    return [...this.alerts];
  }

  /**
   * Get alerts for a specific token
   */
  getAlertsForToken(tokenId: string): PriceAlert[] {
    return this.alerts.filter(a => a.tokenId === tokenId);
  }

  /**
   * Get portfolio alerts
   */
  getPortfolioAlerts(): PriceAlert[] {
    return this.alerts.filter(a =>
      a.alertScope === 'portfolio' || a.tokenId === 'portfolio'
    );
  }

  /**
   * Get ATH alerts
   */
  getATHAlerts(): PriceAlert[] {
    return this.alerts.filter(a =>
      a.condition === 'token_ath' || a.condition === 'portfolio_ath'
    );
  }

  /**
   * Get cooldown remaining time in minutes
   */
  getCooldownRemaining(alert: PriceAlert): number {
    if (!alert.cooldownUntil) return 0;

    const now = new Date();
    const cooldownExpiry = new Date(alert.cooldownUntil);
    const remainingMs = cooldownExpiry.getTime() - now.getTime();

    if (remainingMs <= 0) return 0;

    return Math.ceil(remainingMs / (1000 * 60)); // Convert to minutes
  }

  /**
   * Clear all triggered alerts
   * For repeatable alerts: reset them instead of deleting
   * For one-time alerts: delete them
   */
  clearTriggeredAlerts(): void {
    this.alerts = this.alerts.filter(a => {
      if (!a.triggeredAt) return true; // Keep non-triggered alerts

      if (a.repeatable) {
        // Reset repeatable alerts instead of deleting
        a.enabled = true;
        a.triggeredAt = undefined;
        a.notificationSent = false;
        a.cooldownUntil = undefined;
        return true; // Keep the alert
      }

      // Delete non-repeatable triggered alerts
      return false;
    });
    this.save();
  }

  /**
   * Get alert condition display text
   */
  getConditionDisplayText(alert: PriceAlert): string {
    switch (alert.condition) {
      case 'above':
        return `> $${alert.targetPrice?.toLocaleString()}`;
      case 'below':
        return `< $${alert.targetPrice?.toLocaleString()}`;
      case 'change_percent':
        const sign = (alert.changePercent || 0) > 0 ? '+' : '';
        return `${sign}${alert.changePercent}%`;
      case 'token_ath':
        return 'Nouvel ATH';
      case 'portfolio_ath':
        return 'Portfolio ATH';
      case 'portfolio_change':
        const direction = alert.thresholdDirection || 'both';
        const dirText = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '↕';
        return `${dirText} ±${alert.changePercent}%`;
      default:
        return '';
    }
  }

  /**
   * Get alert type badge color
   */
  getAlertTypeColor(alert: PriceAlert): string {
    switch (alert.condition) {
      case 'above':
        return '#22c55e'; // Green
      case 'below':
        return '#ef4444'; // Red
      case 'change_percent':
        return '#3b82f6'; // Blue
      case 'token_ath':
      case 'portfolio_ath':
        return '#fbbf24'; // Gold
      case 'portfolio_change':
        return '#8b5cf6'; // Purple
      default:
        return '#a1a1aa';
    }
  }

  /**
   * Save alerts to localStorage
   */
  private save() {
    StorageService.set('price_alerts', this.alerts);
  }

  /**
   * Cleanup on unmount
   */
  destroy() {
    this.stopChecking();
    this.isInitialized = false;
  }
}

// Singleton instance
export const alertService = new AlertService();
